import { useMemo } from 'react';

import { type Meta, getCampaignMatch } from '@vakantiesnl/services/src/hooks/useSearchUrlBuilder';
import {
	DEFAULT_SORT_ORDER_OPTION,
	type FilterState,
	getSelectedDurations,
} from '@vakantiesnl/services/src/stores/filtersStore';
import type { GlobalFiltersState } from '@vakantiesnl/services/src/stores/globalFiltersStore/interfaces';
import { DURATION_CONFIG_OPTIONS } from '@vakantiesnl/services/src/util/durations';
import { DEFAULT_PARTY, formatPaxQuery } from '@vakantiesnl/services/src/util/partyUtils';
import { isEqual } from 'lodash';

const onlyNumbersRegex = /^\d+$/;

/**
 * Used to build the search url, this function already pre-filters/formats the relevant data that should be part of the url
 * Summary of the flow to build the search url:
 * 1. Create Meta object which includes all filters that should be in the url, either as pathname or as query param
 * 2. Decide based on this Meta obj what should be in the Pathname (e.g. country should only be in path name when there is max 1 of any geo selected)
 * 3. Decide what should be in the query params based on the Meta and Pathname (basically anything that's leftover + any extra query params not used by search such as utm stuff)
 */
export const getPopulatedMeta = (filters: FilterState, globalFilters: GlobalFiltersState): Meta => {
	const meta: Meta = {};
	const durations = getSelectedDurations(globalFilters, filters);
	const isOwnTransport = filters.transport === 'OWN';

	// Make sure this is done before any overrides happen below
	if (durations && durations !== DURATION_CONFIG_OPTIONS.default.string) {
		meta.durations = durations;
	}

	if (filters.page && filters.page !== 1) {
		meta.page = filters.page.toString();
	}

	if (!isEqual(globalFilters.party, DEFAULT_PARTY)) {
		meta.pax = formatPaxQuery(globalFilters.party);
	}

	if (filters.sort && filters.sort !== DEFAULT_SORT_ORDER_OPTION) {
		meta.sort = filters.sort;
	}

	if (!isEqual(globalFilters.party, DEFAULT_PARTY)) {
		meta.pax = formatPaxQuery(globalFilters.party);
	}

	/** Only add these filters when including_flight transport filter is applied  */
	if (!isOwnTransport) {
		if (filters.departureAirport?.length) {
			meta.airports = filters.departureAirport;
		}

		if (filters.tourOperators?.length > 0) {
			meta.tourOperators = filters.tourOperators;
		}
	} else {
		meta.transport = filters.transport;
	}

	if (filters.awards?.length) {
		meta.awards = filters.awards;
	}

	if (filters.distances?.length) {
		meta.distances = filters.distances;
	}

	if (filters.facilities?.length) {
		meta.facilities = filters.facilities;
	}

	if (filters.rating) {
		meta.rating = filters.rating;
	}

	if (filters.stars?.length) {
		meta.stars = filters.stars;
	}

	if (filters.departureAirport?.length) {
		meta.airports = filters.departureAirport;
	}

	if (filters.themes?.length) {
		/** Needs to be equal to the route query name {:themes} */
		meta.themes = filters.themes;
	}

	const isCityTrip = filters.campaign === 'stedentrip';

	if (isCityTrip) {
		if (durations === DURATION_CONFIG_OPTIONS.s.string) {
			meta.durations = undefined;
		} else {
			meta.durations = durations;
		}
	}

	/**
	 * Start: Only add specific filters, which are configurable in a campaign,
	 * when they are not part of the selected campaign
	 **/
	const campaignMatcher = getCampaignMatch(filters);

	if (campaignMatcher.hasValidCampaignMatch) {
		meta.campaign = filters.campaign;
	}

	// Only set mealplans when there is no match in the campaign
	if (!campaignMatcher.mealplans) {
		if (!isOwnTransport && filters.mealplans?.length) {
			meta.mealplans = filters.mealplans;
		}
	}

	// Only set departureDate when there is no match in the campaign
	if (!campaignMatcher.departureDate) {
		if (filters.departureDate) {
			meta.departure_date = filters.departureDate;
		}

		if (filters.flexibleDepartureDate) {
			meta.flexible_departure_date = String(filters.flexibleDepartureDate);
		}
	}

	// Only set geo when there is no match in the campaign
	if (!campaignMatcher.countries) {
		if (filters.countries.length) {
			meta.countries = filters.countries;
		}

		if (filters.regions.length) {
			meta.regions = filters.regions;
		}

		if (filters.cities.length) {
			meta.cities = filters.cities;
		}
	}

	// Only set attributes when there is no match in the campaign
	if (!campaignMatcher.attributes || !campaignMatcher.hasValidCampaignMatch) {
		if (filters.attributes?.length) {
			meta.attributes = filters.attributes;
		}
	}

	// Only set accoType when there is no match in the campaign
	if (!campaignMatcher.accoType) {
		if (filters.accoType?.length) {
			meta.accoType = filters.accoType;
		}
	}

	if (!isOwnTransport) {
		// Only set minPrice when there is no match in the campaign
		if (!campaignMatcher.minPrice && filters.priceMin && onlyNumbersRegex.test(filters.priceMin)) {
			meta.min_price = filters.priceMin;
		}

		// Only set maxPrice when there is no match in the campaign
		if (!campaignMatcher.maxPrice && filters.priceMax && onlyNumbersRegex.test(filters.priceMax)) {
			meta.max_price = filters.priceMax;
		}
	}
	/** End: Only add certain filters when they are not part of the selected campaign */

	if (filters.isDealsRoute) {
		meta.deals = String(filters.isDealsRoute);
		meta.pax = undefined;
	}

	if (filters.chains?.length > 0) {
		meta.chains = filters.chains;
	}

	if (!!filters.airlines.length) {
		meta.airlines = filters.airlines;
	}

	if (!!filters.flightProvider) {
		meta.flight_provider = filters.flightProvider;
	}

	return meta;
};

export const useSearchMeta = (filters: FilterState, globalFilters: GlobalFiltersState): Meta => {
	/** Populate a meta object to build url's/queries or reset then no filters are selected */
	return useMemo(() => getPopulatedMeta(filters, globalFilters), [filters, globalFilters]);
};
